import type { CustomerProfileView, HotelLoyaltyPrograms } from '@voll-intelligence/domain';

import { t } from '@web/i18n';
import type { CardCustomerProfileState } from '@web/ui/core/hooks';
import { Dash, Mono, Row } from '@web/ui/core/molecules';
import { hotelLoyaltyLines, mileagePlanLines } from '@web/ui/core/utils';

/**
 * As quatro linhas de contato do viajante (e-mail, telefone, programas de
 * milhas, programas de hotel) a partir de `useCardCustomerProfile`: skeleton
 * carregando, `—` indisponível. Compartilhadas pela aba Identidade e pelo
 * bloco Cliente da coluna lateral.
 */
export function ContactRows({
  state,
}: Readonly<{ state: CardCustomerProfileState }>): React.JSX.Element {
  return (
    <>
      <Row
        label={t('card.drawer.customer.email')}
        value={
          <ContactValue state={state}>
            {(profile) =>
              profile.email ? (
                <a href={`mailto:${profile.email}`} className="text-primary hover:underline">
                  {profile.email}
                </a>
              ) : (
                <Dash />
              )
            }
          </ContactValue>
        }
      />
      <Row
        label={t('card.drawer.customer.phone')}
        value={
          <ContactValue state={state}>
            {(profile) => (profile.phone ? <Mono>{profile.phone}</Mono> : <Dash />)}
          </ContactValue>
        }
      />
      <Row
        label={t('card.drawer.customer.mileagePlans')}
        multiline
        value={
          <ContactValue state={state}>
            {(profile) => <MonoLines lines={mileagePlanLines(profile)} />}
          </ContactValue>
        }
      />
      <Row
        label={t('card.drawer.customer.hotelLoyalty')}
        multiline
        value={
          <ContactValue state={state}>
            {(_profile, hotelLoyalty) => <MonoLines lines={hotelLoyaltyLines(hotelLoyalty)} />}
          </ContactValue>
        }
      />
    </>
  );
}

/**
 * Corpo de uma linha `multiline` de valores monoespaçados (programas de milhas
 * e de hotel): `—` quando não há nada, senão uma linha por item.
 */
function MonoLines({ lines }: Readonly<{ lines: string[] }>): React.JSX.Element {
  if (lines.length === 0) return <Dash />;
  return (
    <span className="flex flex-col gap-0.5">
      {lines.map((line, index) => (
        <Mono key={`${line}-${String(index)}`}>{line}</Mono>
      ))}
    </span>
  );
}

/**
 * Valor de uma linha de contato: skeleton enquanto o perfil carrega, `—` se
 * ele não vier, senão o render do chamador com o perfil e os programas de
 * fidelidade de hotel prontos.
 */
function ContactValue({
  state,
  children,
}: Readonly<{
  state: CardCustomerProfileState;
  children: (
    profile: CustomerProfileView,
    hotelLoyalty: HotelLoyaltyPrograms | null,
  ) => React.ReactNode;
}>): React.JSX.Element {
  if (state.status === 'loading') return <ContactSkeleton />;
  if (state.status === 'unavailable') return <Dash />;
  return <>{children(state.profile, state.hotelLoyalty)}</>;
}

function ContactSkeleton(): React.JSX.Element {
  return (
    <span
      data-testid="identity-contact-skeleton"
      aria-hidden="true"
      className="block h-4 w-40 animate-pulse rounded-md bg-muted"
    />
  );
}
